import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTable, type TableGroup } from '@/components/ui'
import type { DocumentRow } from '../api/types'
import { DocumentActionsMenu } from './DocumentActionsMenu'
import { CofaIdentityChip } from './CofaIdentityChip'
import { DocumentStatusChip } from './DocumentStatusChip'

export interface DocumentTableProps {
  rows: DocumentRow[]
  onSelect: (row: DocumentRow) => void
  /** Optional: clicking the Vendor CofA / SPI CofA cell calls this
   *  with an explicit kind so the preview drawer opens to the matching
   *  tab. Falls back to `onSelect` for callers that don't need this. */
  onSelectKind?: (row: DocumentRow, tab: 'vendor' | 'spi') => void
  /** Sprint 04 Chunk B — when true, renders the per-row `⋯` actions column. */
  canAct: boolean
  /** Sprint 04 Chunk B — opens the batch uploader. */
  onRequestBatchUpload: (row: DocumentRow) => void
  /** Enables per-user column resize / reorder / persistence + sortable
   *  headers (shared DataTable, Sprint 17). Each CofA page passes its own
   *  id so prefs don't bleed across routes. */
  moduleId?: string
  /** Optional row grouping (group-by-status / vendor / product / date).
   *  When provided, the table renders collapsible group headers; omit for
   *  a flat table. Must be a partition of `rows`. */
  groups?: TableGroup<DocumentRow>[]
  collapsedGroups?: ReadonlySet<string>
  onToggleGroup?: (key: string) => void
  /** First load in flight — shows the table's loading state instead of
   *  its empty "No matches". */
  loading?: boolean
}

export function DocumentTable({
  rows,
  onSelect,
  onSelectKind,
  canAct,
  onRequestBatchUpload,
  moduleId,
  groups,
  collapsedGroups,
  onToggleGroup,
  loading = false,
}: DocumentTableProps) {
  const columns = useMemo<ColumnDef<DocumentRow, unknown>[]>(
    () => [
      {
        id: 'lot_name',
        header: 'Lot #',
        accessorKey: 'lot_name',
        cell: (ctx) => (
          <span className="text-role-data-primary" title={ctx.row.original.lot_name}>
            {ctx.row.original.lot_name}
          </span>
        ),
      },
      {
        id: 'product_name',
        header: 'Product',
        accessorKey: 'product_name',
      },
      {
        id: 'vendor_name',
        header: 'Vendor',
        accessorFn: (r) => r.vendor_name || '',
        cell: (ctx) => ctx.row.original.vendor_name || '(no vendor)',
      },
      {
        id: 'uploaded_at',
        header: 'Uploaded',
        accessorKey: 'uploaded_at',
        cell: (ctx) => ctx.row.original.uploaded_at ?? '—',
      },
      {
        id: 'manufacture_date',
        header: 'Mfg',
        accessorKey: 'manufacture_date',
        cell: (ctx) => ctx.row.original.manufacture_date ?? '—',
      },
      {
        id: 'expiration_date',
        header: 'Expiry',
        accessorKey: 'expiration_date',
        cell: (ctx) => ctx.row.original.expiration_date ?? '—',
      },
      {
        id: 'vendor_cofa',
        header: 'Vendor CofA',
        cell: (ctx) =>
          ctx.row.original.vendor_cofa ? (
            <button
              type="button"
              title={ctx.row.original.vendor_cofa.filename}
              onClick={(e) => {
                e.stopPropagation()
                onSelectKind?.(ctx.row.original, 'vendor')
              }}
              className="truncate cursor-pointer border-none bg-transparent p-0 text-left text-xs text-link hover:underline"
            >
              {`${ctx.row.original.product_name} CofA`}
            </button>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        id: 'spi_cofa',
        header: 'SPI CofA',
        cell: (ctx) =>
          ctx.row.original.spi_cofa ? (
            <button
              type="button"
              title={ctx.row.original.spi_cofa.filename}
              onClick={(e) => {
                e.stopPropagation()
                onSelectKind?.(ctx.row.original, 'spi')
              }}
              className="truncate cursor-pointer border-none bg-transparent p-0 text-left text-xs text-link hover:underline"
            >
              {`${ctx.row.original.product_name} CofA`}
            </button>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (ctx) => <DocumentStatusChip status={ctx.row.original.status} />,
      },
      {
        // The certificate customers receive (SPI CofA for SPI products,
        // vendor otherwise): is it confirmed to be this lot's? Clicking
        // opens the drawer on that certificate, where the check is explained.
        id: 'identity_status',
        header: 'CofA check',
        accessorFn: (r) => r.identity_status ?? '',
        cell: (ctx) => {
          const r = ctx.row.original
          if (!r.identity_status) return <span className="text-tertiary">—</span>
          const required = r.is_spi_product ? r.spi_cofa : r.vendor_cofa
          return (
            <button
              type="button"
              title="Open the customer CofA and see what was checked"
              onClick={(e) => {
                e.stopPropagation()
                onSelectKind?.(r, r.is_spi_product && !r.source_problem ? 'spi' : 'vendor')
              }}
              className="cursor-pointer border-none bg-transparent p-0"
            >
              <CofaIdentityChip status={r.identity_status} identity={required?.identity} sourceProblem={r.source_problem} />
            </button>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        cell: (ctx) => (
          <DocumentActionsMenu
            row={ctx.row.original}
            canAct={canAct}
            onRequestBatchUpload={onRequestBatchUpload}
          />
        ),
      },
    ],
    [canAct, onRequestBatchUpload, onSelectKind],
  )

  return (
    <DataTable
      data={rows}
      columns={columns}
      loading={loading}
      groups={groups}
      collapsedGroups={collapsedGroups}
      onToggleGroup={onToggleGroup}
      getRowId={(r) => String(r.lot_id)}
      onRowClick={onSelect}
      moduleId={moduleId}
      criticalRowPredicate={(row) => row.status === 'missing_vendor' && row.is_spi_product}
      maxHeight="100%"
      columnWidths={{
        lot_name: 200,
        uploaded_at: 120,
        manufacture_date: 110,
        expiration_date: 110,
        status: 110,
        actions: 40,
      }}
      minTableWidth={1000}
    />
  )
}
